/**
 * iter136 / iter146 — Native image acquisition helper for Capacitor
 * Android/iOS.
 *
 * WHY:
 *   The HTML `<input type="file" capture="environment">` element is
 *   unreliable inside the Capacitor Android WebView (first tap opens the
 *   gallery instead of the camera; after cancel the input goes inert).
 *   For CAMERA capture we therefore route through the Capacitor Camera
 *   plugin (native intents, no media permissions required).
 *
 *   For GALLERY selection (iter146 — Google Play "Photo & Video
 *   Permissions" policy compliance) we deliberately do NOT use the
 *   Capacitor Camera plugin's `CameraSource.Photos` path — that path uses
 *   ACTION_GET_CONTENT/ACTION_PICK backed by MediaStore, historically
 *   requiring READ_MEDIA_IMAGES. Instead we return `null` from the
 *   helper so the caller falls back to the standard HTML file input
 *   (`<input type="file" accept="image/*">`). Inside the Capacitor
 *   Android WebView (Chromium 111+, shipped on Android 13+), that
 *   file-input path is automatically delegated to the OS Photo Picker
 *   via PickVisualMedia — no permission required. On older Android it
 *   falls back to ACTION_GET_CONTENT, which returns a scoped content URI
 *   the app can read for the duration of the intent grant, again with
 *   no broad media permissions.
 *
 * CONTRACT:
 *   `acquireImageNative({ source })` returns a `File` object that is
 *   drop-in equivalent to `e.target.files[0]` from the HTML file input.
 *   - source='camera' → invokes native camera intent; returns File or
 *     null on cancel.
 *   - source='gallery' → returns `null` on native so the caller falls
 *     back to the Photo-Picker-backed HTML file input.
 *   - Web (non-native) always returns `null`; caller uses the HTML input.
 *   - Throws on plugin/permission errors — caller handles UX.
 */
import { Capacitor } from '@capacitor/core';
import { Camera, CameraResultType, CameraSource } from '@capacitor/camera';

export const isNativeCapacitor = () => {
  try {
    return Capacitor.isNativePlatform() && Capacitor.getPlatform() !== 'web';
  } catch {
    return false;
  }
};

const MIME_BY_FORMAT = {
  jpeg: 'image/jpeg',
  jpg: 'image/jpeg',
  png: 'image/png',
  webp: 'image/webp',
};

const dataUrlToFile = (dataUrl, filename) => {
  // dataUrl example: "data:image/jpeg;base64,/9j/4AAQ..."
  const [head, b64] = dataUrl.split(',');
  const mimeMatch = /data:([^;]+);base64/.exec(head || '');
  const mime = (mimeMatch && mimeMatch[1]) || 'image/jpeg';
  const bin = atob(b64 || '');
  const bytes = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i += 1) bytes[i] = bin.charCodeAt(i);
  return new File([bytes], filename, { type: mime, lastModified: Date.now() });
};

/**
 * Acquire an image via the native Capacitor camera. For gallery source we
 * deliberately return `null` so the caller uses the OS Photo Picker via
 * the HTML file input (Google Play policy-compliant path).
 *
 * @param {Object} options
 * @param {'camera' | 'gallery'} options.source
 * @returns {Promise<File | null>} File object, or null to signal the
 * caller should use the HTML file input.
 */
export const acquireImageNative = async ({ source = 'camera' } = {}) => {
  if (!isNativeCapacitor()) return null;

  // iter146 — Gallery selection must go through the Android Photo Picker
  // to satisfy Google Play's Photo & Video Permissions policy. The
  // WebView delegates `<input type="file" accept="image/*">` to
  // PickVisualMedia on Android 13+ without any media permission. Signal
  // "please use the HTML input" by returning null.
  if (source === 'gallery') return null;

  try {
    const photo = await Camera.getPhoto({
      quality: 85,
      allowEditing: false,
      // Data-URL result → we hand a File back to the existing pipeline
      // without touching the upload layer.
      resultType: CameraResultType.DataUrl,
      source: CameraSource.Camera,
      // JPEG keeps parity with the existing web capture flow.
      saveToGallery: false,
      correctOrientation: true,
      presentationStyle: 'fullscreen',
    });

    if (!photo || !photo.dataUrl) return null;

    const ext = (photo.format || 'jpeg').toLowerCase();
    const mime = MIME_BY_FORMAT[ext] || 'image/jpeg';
    const filename = `guest-doc-camera-${Date.now()}.${ext === 'jpg' ? 'jpg' : ext}`;

    const file = dataUrlToFile(photo.dataUrl, filename);
    // The File constructor honours the mime we detected from dataUrl;
    // some platforms report `image/pjpeg` etc — normalise to accepted types.
    if (!['image/jpeg', 'image/png', 'image/webp'].includes(file.type)) {
      return new File([file], filename, { type: mime, lastModified: file.lastModified });
    }
    return file;
  } catch (err) {
    // Cancellation surfaces as an error on some platforms — normalise to null.
    const msg = String(err?.message || err || '').toLowerCase();
    if (msg.includes('cancel') || msg.includes('user denied') || msg.includes('no image picked')) {
      return null;
    }
    throw err;
  }
};

/**
 * Convenience wrapper that produces a *fake* change event compatible with
 * an <input type="file"> handler, so callers can reuse their existing
 * onChange handler verbatim.
 */
export const makeFakeFileEvent = (file) => ({
  target: {
    files: file ? [file] : [],
    value: '',
  },
});
